import React, { useEffect, useState, useContext } from 'react';
import { IRootState } from 'src/reducers';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import { useFetch } from 'use-http';
import Card from 'src/components/common/Card.redesign';
import VisibilityLoader from 'src/components/common/VisibilityLoader';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import EasternMap from 'src/models/EasternMap';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import MathUtil from 'src/util/MathUtil';

interface IMapCardProps {
    mapNumber: number;
    townshipId: number;
    isActive: boolean;
    onMapSelected: (mapNumber: number) => void;
}

const MapCard: React.FC<IMapCardProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const [map, setMap] = useState<EasternMap>(undefined);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [mapAcreage, setMapAcreage] = useState(undefined);
    const { get, response } = useFetch();

    const dialogContext = useContext<dialogCreators>(DialogContext);

    const loadData = async (isVisible: boolean) => {
        if (isVisible && !map) {
            const mapCall = get(Url.project.getEasternMap(appContext, props.mapNumber));
            const acreageCall = get(Url.project.getEasternMapAcreage(appContext, props.mapNumber));

            const [mapResult, acreageResult] = [await mapCall, await acreageCall];

            if (response.ok) {
                setMap(mapResult);
                setMapAcreage(acreageResult);
            }
        }
    };

    const acreage = () => {
        let data: React.ReactElement = undefined;
        if (!mapAcreage) {
            data = <span>Loading...</span>;
        } else {
            const grossAcres = MathUtil.cleanNumber(mapAcreage.grossAcres as number, 3);
            const hbpAcres = MathUtil.cleanNumber(mapAcreage.hbpAcres as number, 3);
            const leasedAcres = MathUtil.cleanNumber(mapAcreage.leasedAcres as number, 3);
            const openAcres = MathUtil.cleanNumber(mapAcreage.openAcres as number, 3);

            data = (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        fontSize: '.7em',
                        background: '#ccc',
                        color: '#555',
                        fontWeight: 'bold',
                    }}
                >
                    <span style={{ textAlign: 'left', padding: '.2rem .3rem', width: '100%' }}>Gross</span>
                    <span style={{ textAlign: 'right', padding: '.2rem .3rem', color: '#6393C1', background: '#f1f1f1', width: '100%' }}>
                        {grossAcres}
                    </span>
                    <span style={{ textAlign: 'left', padding: '.2rem .3rem', width: '100%' }}>HBP</span>
                    <span style={{ textAlign: 'right', padding: '.2rem .3rem', color: '#6393C1', background: '#f1f1f1', width: '100%' }}>
                        {hbpAcres}
                    </span>
                    <span style={{ textAlign: 'left', padding: '.2rem .3rem', width: '100%' }}>Open</span>
                    <span style={{ textAlign: 'right', padding: '.2rem .3rem', color: '#6393C1', background: '#f1f1f1', width: '100%' }}>
                        {openAcres}
                    </span>
                    <span style={{ textAlign: 'left', padding: '.2rem .3rem', width: '100%' }}>Leased</span>
                    <span style={{ textAlign: 'right', padding: '.2rem .3rem', color: '#6393C1', background: '#f1f1f1', width: '100%' }}>
                        {leasedAcres}
                    </span>
                </div>
            );
        }

        return (
            <div className="acreage" style={{ width: '100%', position: 'relative', border: '1px solid #ccc', borderWidth: '1px 0' }}>
                <span className="grid">{data}</span>
            </div>
        );
    };

    const content = () => {
        let sectionContent: React.ReactElement = undefined;

        if (!dataLoaded) {
            sectionContent = <div>Loading...</div>;
        } else if (!map) {
            sectionContent = <div>Map Not Found</div>;
        } else {
            sectionContent = (
                <>
                    <div style={{ padding: '.4rem', display: 'flex', marginTop: '.5rem' }}>
                        <div className="title" style={{ width: '10rem', fontSize: '.9em' }}>
                            Map {map.name}
                        </div>
                        {acreage()}
                    </div>
                </>
            );
        }

        return sectionContent;
    };

    useEffect(() => {
        if (map) {
            setDataLoaded(true);
        }
    }, [map]);

    const reloadMap = async () => {
        const mapCall = get(Url.project.getEasternMap(appContext, props.mapNumber));
        const acreageCall = get(Url.project.getEasternMapAcreage(appContext, props.mapNumber));

        const [mapResult, acreageResult] = [await mapCall, await acreageCall];

        if (response.ok) {
            setMap(mapResult);
            setMapAcreage(acreageResult);
        }
    };

    const actions: ContextMenuAction[] = [
        {
            text: 'Modify Map',
            action: () => dialogContext.project.addUpdateEasternMap(props.townshipId, props.mapNumber, 'edit', reloadMap),
        },
    ];

    const rightBadge = () => {
        if (!mapAcreage) {
            return '';
        }

        return (
            <span style={{ fontSize: '.8em', fontWeight: 'bold', color: '#999' }}>
                <span>{`${mapAcreage.tractCount} Tract${mapAcreage.tractCount !== 1 ? 's' : ''}`}</span>
                <span style={{ marginLeft: '.5rem' }}>{`${mapAcreage.ownerCount} Owner${mapAcreage.ownerCount !== 1 ? 's' : ''}`}</span>
            </span>
        );
    };

    const cardStyle: React.CSSProperties = {
        padding: '0 0 .2rem 0',
        position: 'relative',
        minHeight: '2.5rem',
    };

    return (
        <Card
            className={`inner-card ${props.isActive ? 'active' : ''}`}
            contextActions={actions}
            newContext={true}
            showMenu={true}
            onClick={() => props.onMapSelected(props.mapNumber)}
            badge={`${map && map.dlcNumber}`}
            rightBadgePanel={rightBadge()}
        >
            <VisibilityLoader onVisibilityChanged={loadData}>
                <div style={cardStyle}>{content()}</div>
            </VisibilityLoader>
        </Card>
    );
};

export default MapCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/MapCard.tsx